/** Pure helpers of the admin Library (no React): where a page lives, the stack page's cost totals, the ECLIPSE
 * limits filter and cohort maths. Tested in library.test.ts. */

export interface LibraryItemLike {
  slug: string;
  group: string;
  title: string;
}
export interface LibraryIndexLike<I extends LibraryItemLike = LibraryItemLike> {
  groups: { slug: string; title: string; description: string }[];
  items: I[];
}

export const LIBRARY_ROOT = "/admin/library";

/** A group with one page opens that page directly: /admin/library/<group>; otherwise /admin/library/<group>/<slug>. */
export function libraryPath(index: LibraryIndexLike, item: LibraryItemLike): string {
  const siblings = index.items.filter((i) => i.group === item.group);
  return siblings.length === 1 ? `${LIBRARY_ROOT}/${item.group}` : `${LIBRARY_ROOT}/${item.group}/${item.slug}`;
}

export type Resolved<I extends LibraryItemLike> =
  | { kind: "group"; group: LibraryIndexLike["groups"][number]; items: I[] }
  | { kind: "page"; group: LibraryIndexLike["groups"][number]; item: I }
  | { kind: "missing" };

/** What /admin/library/:group(/:slug) shows. */
export function resolveLibrary<I extends LibraryItemLike>(index: LibraryIndexLike<I>, groupSlug: string, slug?: string): Resolved<I> {
  const group = index.groups.find((g) => g.slug === groupSlug);
  if (!group) return { kind: "missing" };
  const items = index.items.filter((i) => i.group === group.slug);
  if (slug) {
    const item = items.find((i) => i.slug === slug);
    return item ? { kind: "page", group, item } : { kind: "missing" };
  }
  if (items.length === 1) return { kind: "page", group, item: items[0] };
  return { kind: "group", group, items };
}

// ------------------------------------------------------------------ stack and costs
export interface StackScale {
  key: string;
  label: string;
  companies: string;
  note: string;
  revenue?: number;
}
export interface StackItem {
  name: string;
  does: string;
  tech?: string;
  where: string;
  plan: string;
  kind: "free" | "fixed" | "usage" | "revenue";
  cost: Record<string, number>;
  basis: string;
}
export interface StackGroup {
  key: string;
  title: string;
  items: StackItem[];
}
export interface StackData {
  as_of: string;
  currency: string;
  scales: StackScale[];
  assumptions: string[];
  groups: StackGroup[];
  sources: { label: string; url: string; checked: string }[];
}

export function groupTotal(group: StackGroup, scale: string): number {
  return group.items.reduce((sum, item) => sum + (item.cost[scale] ?? 0), 0);
}

export function stackTotal(data: StackData, scale: string): number {
  return data.groups.reduce((sum, group) => sum + groupTotal(group, scale), 0);
}

/** Monthly cost per company at a scale (null for "today", which has no company count). */
export function perCompany(data: StackData, scale: string): number | null {
  const n = Number(scale.replace(/^c/, ""));
  return Number.isFinite(n) && n > 0 ? stackTotal(data, scale) / n : null;
}

export function usd(value: number, digits?: number, locale = "en-US"): string {
  const d = digits ?? (value !== 0 && Math.abs(value) < 10 ? 2 : 0);
  return value.toLocaleString(locale, { style: "currency", currency: "USD", minimumFractionDigits: d, maximumFractionDigits: d });
}

// ------------------------------------------------------------------ ECLIPSE limits
export interface LimitRow {
  area: string;
  name: string;
  free: string;
  pro: string;
  business: string;
  enterprise: string;
  kind: string;
  enforced: string;
  unit_cost: string;
  worst_free: string;
  today: string;
  changed: number;
}
export interface LimitFilter {
  q: string;
  area: string;
  kind: string;
  changed: "" | "1" | "2";
}

export function filterLimits(rows: LimitRow[], f: LimitFilter): LimitRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!f.area || r.area === f.area) &&
      (!f.kind || r.kind === f.kind) &&
      (!f.changed || (f.changed === "2" ? r.changed === 2 : r.changed > 0)) &&
      (!q || Object.values(r).join(" ").toLowerCase().includes(q)),
  );
}

/** Cumulative revenue of a 100-customer cohort, month by month, churn compounding monthly (the plan's model). */
export function cohortSeries(price: number, churn: number, months = 36, customers = 100): number[] {
  const out: number[] = [];
  let cumulative = 0;
  for (let t = 0; t < months; t++) {
    cumulative += customers * Math.pow(1 - churn, t) * price;
    out.push(cumulative);
  }
  return out;
}

export function compactUsd(value: number): string {
  return value >= 1e6 ? `$${(value / 1e6).toFixed(1)}M` : `$${Math.round(value / 1e3)}k`;
}

// ------------------------------------------------------------------ ECLIPSE revision 2: the live plan catalogue
/** One catalogue item (backend domain/plans.py, read live from GET /v1/admin/plans) as the explorer shows it. */
export interface PlanRow {
  key: string;
  area: string;
  label: string;
  kind: string;
  description: string;
  published: boolean;
  /** Plan key → the value as text ("$149", "35.00 Lux", "U 10 · D 20 · B 30", "unlimited"). */
  values: Record<string, string>;
  /** Plans whose stored value differs from the ECLIPSE default (an admin edit). */
  edited: string[];
  /** Plans where the limit is switched off (not enforced). */
  off: string[];
}
export interface PlanFilter {
  q: string;
  area: string;
  kind: string;
  /** "published": only values on the pricing page or in the Terms; "edited": only admin edits. */
  only: "" | "published" | "edited";
}

export function filterPlanRows(rows: PlanRow[], f: PlanFilter): PlanRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!f.area || r.area === f.area) &&
      (!f.kind || r.kind === f.kind) &&
      (f.only !== "published" || r.published) &&
      (f.only !== "edited" || r.edited.length > 0) &&
      (!q || [r.key, r.area, r.label, r.description, ...Object.values(r.values)].join(" ").toLowerCase().includes(q)),
  );
}

// ------------------------------------------------------------------ data sources
export interface SourceRow {
  name: string;
  area: string;
  provider: string;
  kind: string;
  what: string;
  used_for: string;
  refresh: string;
  schedule: string;
  licence: string;
  code: string;
  notes?: string;
}

export function filterSources(rows: SourceRow[], q: string, area: string, kind: string): SourceRow[] {
  const needle = q.trim().toLowerCase();
  return rows.filter(
    (r) => (!area || r.area === area) && (!kind || r.kind === kind) && (!needle || Object.values(r).join(" ").toLowerCase().includes(needle)),
  );
}

/** Refresh mode in one word, for the sources table's badge. */
export function refreshMode(refresh: string): "automatic" | "manual" | "live" | "none" {
  const r = refresh.toLowerCase();
  if (r.startsWith("automatic")) return "automatic";
  if (r.startsWith("live") || r.startsWith("per ")) return "live";
  if (r === "—" || r === "") return "none";
  return "manual";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// ------------------------------------------------------------------ live test cases (Quality)
/** library/content/test-cases.yaml, test-results.yaml and exploratory.yaml, merged by the API. */
export type TestLayer = "unit" | "integration" | "e2e" | "live";
export type TestResultValue = "pass" | "fail" | "blocked" | "not run";
export interface TestCase {
  id: string;
  area: string;
  title: string;
  why: string;
  preconditions: string[];
  steps: string[];
  expected: string[];
  automated: boolean;
  script?: string;
  blocked_on?: string;
  layers: { layer: TestLayer; test: string; covers: string }[];
}
export interface TestResult {
  id: string;
  environment: string;
  result: TestResultValue;
  date: string;
  api_build: string;
  dashboard_build: string;
  duration_s?: number;
  script?: string;
  notes: string;
  link?: string;
  metrics?: Record<string, number>;
}
export interface TestRun {
  environment: string;
  date: string;
  api_build: string;
  dashboard_build: string;
  cases: string[];
  lux_spent: number;
}
export type Severity = "blocker" | "major" | "minor" | "cosmetic";
export interface Finding {
  title: string;
  severity: Severity;
  steps: string[];
  seen: string;
  expected: string;
  screenshot?: string;
  status: "fixed" | "written up" | "not a bug";
  fix?: string;
  link?: string;
}
export interface Session {
  date: string;
  build: { api: string; dashboard: string };
  duration_min: number;
  tried: string[];
  findings: Finding[];
  notes?: string;
}
export interface Charter {
  id: string;
  area: string;
  mission: string;
  vary: string[];
  risks: string[];
  sessions: Session[];
}
export interface TestsData {
  areas: string[];
  layers: Record<TestLayer, string>;
  cases: TestCase[];
  results: TestResult[];
  runs: TestRun[];
  severities: Severity[];
  charters: Charter[];
}

export const RESULT_ORDER: TestResultValue[] = ["fail", "blocked", "pass", "not run"];
export const LAYER_ORDER: TestLayer[] = ["unit", "integration", "e2e", "live"];

/** The latest result of a case on an environment ("not run" when it has none). */
export function latestResult(results: TestResult[], id: string, environment: string): TestResult | null {
  return results.find((r) => r.id === id && r.environment === environment) ?? null;
}

export function resultOf(results: TestResult[], id: string, environment: string): TestResultValue {
  return latestResult(results, id, environment)?.result ?? "not run";
}

/** The cases the filters show: an area (or all) and a last result (or all). */
export function filterCases(data: Pick<TestsData, "cases" | "results">, f: { area: string; result: "" | TestResultValue; environment: string }): TestCase[] {
  return data.cases.filter((c) => (!f.area || c.area === f.area) && (!f.result || resultOf(data.results, c.id, f.environment) === f.result));
}

/** How many cases have each last result, in RESULT_ORDER. */
export function resultCounts(data: Pick<TestsData, "cases" | "results">, environment: string): [TestResultValue, number][] {
  return RESULT_ORDER.map((r) => [r, data.cases.filter((c) => resultOf(data.results, c.id, environment) === r).length]);
}

/** The layers that cover a case, fastest first, each once. */
export function caseLayers(c: Pick<TestCase, "layers">): TestLayer[] {
  return LAYER_ORDER.filter((l) => c.layers.some((x) => x.layer === l));
}

/** Findings of a charter's sessions per severity (blocker first), for its heading. */
export function findingCounts(charter: Charter): [Severity, number][] {
  const all = charter.sessions.flatMap((s) => s.findings);
  return (["blocker", "major", "minor", "cosmetic"] as Severity[]).map((s) => [s, all.filter((f) => f.severity === s).length] as [Severity, number]).filter(([, n]) => n > 0);
}

/** "2026-10-13" → "13 Oct 2026" (a calendar date: no time-zone shift). */
export function formatDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : value;
}
